import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Sun, Moon, ChevronDown } from "lucide-react";
import { LogoLockup } from "./Logo";
import { PRODUCTS, INDUSTRIES } from "../data/products";

const SIMPLE_ITEMS = [
  { to: "/about", label: "About" },
  { to: "/resources", label: "Resources" },
  { to: "/contact", label: "Contact" },
];

export function Nav({ theme, setTheme }) {
  const [scrolled, setScrolled] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 24);
      const doc = document.documentElement;
      const max = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.max(0, Math.min(100, (y / max) * 100));
      setProgress(pct);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
    <header className={"nav " + (scrolled ? "nav--scrolled" : "")} data-testid="main-nav">
      <div className="nav-inner">
        <Link to="/" aria-label="Capexara AI home" data-testid="nav-home-link">
          <LogoLockup />
        </Link>
        <nav className="nav-links" data-testid="nav-links">
          <NavLink to="/about" className={({ isActive }) => (isActive ? "active" : "")} data-testid="nav-link-about">
            About
          </NavLink>
          <NavLink to="/what-we-do" className={({ isActive }) => (isActive ? "active" : "")} data-testid="nav-link-what-we-do">
            What we do
          </NavLink>
          <PlatformsMenu />
          <IndustriesMenu />
          <NavLink to="/resources" className={({ isActive }) => (isActive ? "active" : "")} data-testid="nav-link-resources">
            Resources
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => (isActive ? "active" : "")} data-testid="nav-link-contact">
            Contact
          </NavLink>
        </nav>
        <div className="nav-right">
          <ThemeToggle theme={theme} setTheme={setTheme} />
          <Link
            to="/contact"
            className="btn btn-primary"
            style={{ padding: "10px 18px", fontSize: 14 }}
            data-testid="nav-cta-request-demo"
          >
            Request demo <span className="arrow">→</span>
          </Link>
        </div>
      </div>
      <div
        className="nav-progress"
        style={{ width: `${progress}%` }}
        role="progressbar"
        aria-label="Page scroll progress"
        aria-valuenow={Math.round(progress)}
        aria-valuemin={0}
        aria-valuemax={100}
        data-testid="nav-scroll-progress"
      />
    </header>
  );
}

function PlatformsMenu() {
  const catA = PRODUCTS.filter((p) => p.cat === "A");
  const catB = PRODUCTS.filter((p) => p.cat === "B");
  return (
    <div className="nav-dd" data-testid="nav-platforms-menu">
      <NavLink to="/platforms" className={({ isActive }) => "nav-dd-trigger " + (isActive ? "active" : "")} data-testid="nav-link-platforms">
        Platforms <ChevronDown size={14} className="nav-dd-chev" />
      </NavLink>
      <div className="nav-dd-panel nav-dd-panel--wide" data-testid="nav-platforms-panel">
        <div className="nav-dd-col">
          <div className="nav-dd-label">Category A · Consulting IP</div>
          {catA.map((p) => (
            <Link key={p.slug} to={`/platforms/${p.slug}`} className="nav-dd-item" data-testid={`nav-platform-${p.slug}`}>
              <div className="nav-dd-item-title">
                <span className="nav-dd-item-name">{p.indo}</span>
                <span className="nav-dd-item-dev">{p.english}</span>
              </div>
              <div className="nav-dd-item-sub">{p.name}</div>
            </Link>
          ))}
        </div>
        <div className="nav-dd-col">
          <div className="nav-dd-label">Category B · Enterprise Platforms</div>
          {catB.map((p) => (
            <Link key={p.slug} to={`/platforms/${p.slug}`} className="nav-dd-item" data-testid={`nav-platform-${p.slug}`}>
              <div className="nav-dd-item-title">
                <span className="nav-dd-item-name">{p.indo}</span>
                <span className="nav-dd-item-dev">{p.english}</span>
              </div>
              <div className="nav-dd-item-sub">{p.name}</div>
            </Link>
          ))}
        </div>
        <div className="nav-dd-footer">
          <Link to="/platforms" className="nav-dd-footer-cta" data-testid="nav-platforms-all">
            Explore all eight platforms <span className="arrow">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function IndustriesMenu() {
  return (
    <div className="nav-dd" data-testid="nav-industries-menu">
      <NavLink to="/industries" className={({ isActive }) => "nav-dd-trigger " + (isActive ? "active" : "")} data-testid="nav-link-industries">
        Industries <ChevronDown size={14} className="nav-dd-chev" />
      </NavLink>
      <div className="nav-dd-panel" data-testid="nav-industries-panel">
        <div className="nav-dd-col">
          <div className="nav-dd-label">Industries served</div>
          {INDUSTRIES.map((ind) => (
            <Link key={ind.key} to="/industries" className="nav-dd-item" data-testid={`nav-industry-${ind.key}`}>
              <div className="nav-dd-item-title">
                <span className="nav-dd-item-name">{ind.name}</span>
              </div>
              <div className="nav-dd-item-sub">{ind.segs.join(" · ")}</div>
            </Link>
          ))}
        </div>
        <div className="nav-dd-footer">
          <Link to="/industries" className="nav-dd-footer-cta" data-testid="nav-industries-all">
            See the industry × product matrix <span className="arrow">→</span>
          </Link>
        </div>
      </div>
    </div>
  );
}

function ThemeToggle({ theme, setTheme }) {
  const flip = (e) => {
    const x = e.clientX, y = e.clientY;
    const newT = theme === "dark" ? "light" : "dark";
    const overlay = document.createElement("div");
    overlay.style.cssText = `position:fixed;inset:0;pointer-events:none;z-index:9999;background:${newT === "dark" ? "#0A0E1A" : "#FFFFFF"};clip-path:circle(0px at ${x}px ${y}px);transition:clip-path 600ms cubic-bezier(.2,.8,.2,1);`;
    document.body.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.style.clipPath = `circle(${Math.hypot(window.innerWidth, window.innerHeight)}px at ${x}px ${y}px)`;
    });
    setTimeout(() => {
      setTheme(newT);
      requestAnimationFrame(() => {
        overlay.style.transition = "opacity 300ms";
        overlay.style.opacity = "0";
        setTimeout(() => overlay.remove(), 300);
      });
    }, 400);
  };
  return (
    <button className="theme-toggle" onClick={flip} aria-label="Toggle theme" data-testid="theme-toggle-button">
      {theme === "dark" ? <Sun size={17} /> : <Moon size={17} />}
    </button>
  );
}
